import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { USER_ROLES, UserRole } from '../constants/roles';

/**
 * Centralized permissions hook
 * Provides permission checks based on user role
 * 
 * Role definitions:
 * - ROOT (0): Full access, can do everything
 * - EDITOR (1): Can edit data, cannot download or manage users
 * - VIEWER (2): Read-only access, cannot edit or download
 * - ADMIN (3): Can edit, download, and manage users (but not root users)
 */
export const usePermissions = () => {
  const { user } = useAuth();
  const role = user?.role as UserRole | undefined;

  return useMemo(() => {
    // Viewer (2) cannot edit, others can
    const canEdit = role !== USER_ROLES.VIEWER;
    
    // Only root (0) and admin (3) can download (editor and viewer cannot)
    const canDownload = role === USER_ROLES.ROOT || role === USER_ROLES.ADMIN;
    
    // Can manage users: root, editor, admin (not viewer)
    const canManageUsers = role !== USER_ROLES.VIEWER;
    
    // Role checks
    const isRoot = role === USER_ROLES.ROOT;
    const isEditor = role === USER_ROLES.EDITOR;
    const isViewer = role === USER_ROLES.VIEWER;
    const isAdmin = role === USER_ROLES.ADMIN;

    return {
      canEdit,
      canDownload,
      canManageUsers,
      isRoot,
      isEditor,
      isViewer,
      isAdmin,
      role, // Expose raw role for any custom checks
    };
  }, [role]);
};

